import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, ChevronDown, ChevronUp, Bell, Plus, X, Trash2, LineChart } from "lucide-react";

const FLAGS = { USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", JPY: "🇯🇵", CNY: "🇨🇳" };

const MOVERS = [
  { icon: "🏦", label: "Interest Rates", desc: "Central bank rate decisions" },
  { icon: "📊", label: "Inflation", desc: "CPI, PPI and price indexes" },
  { icon: "👥", label: "Employment", desc: "Jobs, unemployment, NFP" },
  { icon: "📈", label: "GDP", desc: "Gross Domestic Product" },
  { icon: "🛍️", label: "Retail Sales", desc: "Consumer spending data" },
];

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(date) { return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`; }

function daysUntilLabel(dateStr) {
  const target = new Date(dateStr + "T00:00:00Z");
  const now = new Date();
  const diff = Math.ceil((target - now) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return `In ${diff} days`;
}

function groupByDate(events) {
  const groups = {};
  events.forEach((e) => { (groups[e.event_date] = groups[e.event_date] || []).push(e); });
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

function ImpactDots({ impact }) {
  const level = impact === "high" ? 3 : impact === "medium" ? 2 : 1;
  const color = impact === "high" ? "#f87171" : impact === "medium" ? "#fb923c" : "#facc15";
  return (
    <div>
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: i < level ? color : "var(--border-soft)" }} />
        ))}
      </div>
      <div className="text-[10px] font-mono mt-1 capitalize" style={{ color }}>{impact}</div>
    </div>
  );
}

function Dropdown({ label, value, options, onChange, icon: Icon }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-lg text-sm text-[var(--text-secondary)] hover:border-yellow-400/40 transition">
        {Icon && <Icon className="w-4 h-4 text-[var(--text-tertiary)]" />}
        {value || label}
        <ChevronDown className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-lg shadow-2xl z-20 py-1">
          {options.map((o) => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-yellow-400 transition">
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AddEventForm({ onAdded, onClose }) {
  const [title, setTitle] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [timeUtc, setTimeUtc] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [impact, setImpact] = useState("medium");
  const [forecast, setForecast] = useState("");
  const [previous, setPrevious] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !eventDate || !description.trim()) return toast.error("Fill in title, date, and description");
    setSaving(true);
    try {
      await api.post("/calendar/events", {
        title, category: "macro", event_date: eventDate, description,
        impact, currency, time_utc: timeUtc || null,
        forecast: forecast || null, previous: previous || null,
      });
      toast.success("Event added");
      setTitle(""); setEventDate(""); setTimeUtc(""); setForecast(""); setPrevious(""); setDescription("");
      onAdded();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add event");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-[var(--bg-panel)] border border-yellow-400/30 rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-[var(--text-primary)]">Add Economic Event</h3>
        <button type="button" onClick={onClose} className="text-[var(--text-tertiary)] hover:text-yellow-400 transition"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title (e.g. CPI m/m)"
          className="md:col-span-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
        <select value={currency} onChange={(e) => setCurrency(e.target.value)}
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
          {Object.keys(FLAGS).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
        <input type="time" value={timeUtc} onChange={(e) => setTimeUtc(e.target.value)} placeholder="Time (UTC)"
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
        <select value={impact} onChange={(e) => setImpact(e.target.value)}
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
          <option value="high">High Impact</option>
          <option value="medium">Medium Impact</option>
          <option value="low">Low Impact</option>
        </select>
        <input value={forecast} onChange={(e) => setForecast(e.target.value)} placeholder="Forecast (optional)"
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
        <input value={previous} onChange={(e) => setPrevious(e.target.value)} placeholder="Previous (optional)"
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
      </div>
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description..." rows={2}
        className="w-full mt-3 bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 resize-none" />
      <button type="submit" disabled={saving}
        className="mt-4 bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-lg px-6 py-2.5 hover:bg-yellow-500 transition disabled:opacity-50 text-sm">
        {saving ? "Adding..." : "Add Event"}
      </button>
    </form>
  );
}

export default function ForexCalendar() {
  const { user } = useAuth();
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [impactFilter, setImpactFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [collapsed, setCollapsed] = useState(new Set());
  const [expandedRow, setExpandedRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/events", { params: { category: "macro", upcoming_only: true, limit: 300 } });
      setAllEvents(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const removeEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete("/calendar/events/" + id);
      toast.success("Event deleted");
      load();
    } catch { toast.error("Could not delete event"); }
  };

  const filtered = allEvents.filter((e) =>
    (impactFilter === "all" || e.impact === impactFilter) &&
    (currencyFilter === "all" || e.currency === currencyFilter)
  );

  const grouped = useMemo(() => groupByDate([...filtered].sort((a, b) => a.event_date.localeCompare(b.event_date))), [filtered]);

  const highImpactUpcoming = useMemo(() =>
    [...allEvents].filter((e) => e.impact === "high").sort((a, b) => a.event_date.localeCompare(b.event_date)).slice(0, 3),
    [allEvents]
  );

  const currencyOptions = [{ value: "all", label: "All Countries" }, ...Object.keys(FLAGS).map((c) => ({ value: c, label: FLAGS[c] + " " + c }))];
  const impactOptions = [
    { value: "all", label: "All Impact" },
    { value: "high", label: "High Impact" },
    { value: "medium", label: "Medium Impact" },
    { value: "low", label: "Low Impact" },
  ];

  const toggleDay = (date) => {
    const next = new Set(collapsed);
    next.has(date) ? next.delete(date) : next.add(date);
    setCollapsed(next);
  };

  return (
    <div className="pt-6 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">

        {/* Hero */}
        <div
          className="relative rounded-2xl overflow-hidden mb-8 p-8 md:p-10"
          style={{
            backgroundImage: "linear-gradient(90deg, var(--bg-base) 30%, transparent), url('/images/world-map-hero.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "right center",
          }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-yellow-400/15 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-7 h-7 text-yellow-400" />
            </div>
            <div>
              <h1 className="font-display font-bold text-3xl md:text-4xl text-[var(--text-primary)] tracking-tight">
                Economic <span className="text-yellow-400">Calendar</span>
              </h1>
              <p className="mt-1 text-[var(--text-secondary)]">Scheduled economic events that move forex, crypto, and global markets.</p>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Dropdown label="All Impact" value={impactOptions.find((o) => o.value === impactFilter)?.label} options={impactOptions} onChange={setImpactFilter} />
            <Dropdown label="All Countries" value={currencyOptions.find((o) => o.value === currencyFilter)?.label} options={currencyOptions} onChange={setCurrencyFilter} />
            {user?.role === "admin" && (
              <button onClick={() => setShowAdd(!showAdd)}
                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-lg text-sm text-[var(--text-secondary)] hover:border-yellow-400/40 transition">
                <Plus className="w-4 h-4" /> Add Event
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-lg text-sm text-[var(--text-tertiary)]">
            Timezone: <span className="text-[var(--text-primary)] font-bold">UTC</span>
          </div>
        </div>

        {showAdd && <AddEventForm onAdded={load} onClose={() => setShowAdd(false)} />}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* Main table */}
          <div className="lg:col-span-9 space-y-4">
            {loading ? (
              <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-10">Loading events...</p>
            ) : grouped.length === 0 ? (
              <div className="text-center py-16 border border-[var(--border-soft)] bg-[var(--bg-panel)] rounded-xl">
                <p className="text-[var(--text-tertiary)] font-mono text-sm">No upcoming events match this filter.</p>
              </div>
            ) : (
              grouped.map(([date, dayEvents]) => {
                const isCollapsed = collapsed.has(date);
                return (
                  <div key={date} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl overflow-hidden">
                    <button onClick={() => toggleDay(date)}
                      className="w-full flex items-center justify-between px-5 py-3.5 bg-[var(--bg-elevated)] hover:bg-[var(--bg-elevated)]/70 transition">
                      <span className="flex items-center gap-2 font-bold text-sm text-yellow-400">
                        <CalendarDays className="w-4 h-4" />
                        {new Date(date + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="text-xs font-mono text-yellow-400">{daysUntilLabel(date)}</span>
                        {isCollapsed ? <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)]" /> : <ChevronUp className="w-4 h-4 text-[var(--text-tertiary)]" />}
                      </span>
                    </button>
                    {!isCollapsed && (
                    <>
                      <div className="hidden md:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-[var(--border-soft)] text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                            <th className="p-3">Time</th>
                            <th className="p-3">Cur</th>
                            <th className="p-3">Impact</th>
                            <th className="p-3">Event</th>
                            <th className="p-3 text-right">Actual</th>
                            <th className="p-3 text-right">Forecast</th>
                            <th className="p-3 text-right">Previous</th>
                            <th className="p-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayEvents.map((ev) => (
                            <React.Fragment key={ev.id}>
                              <tr className="border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--bg-elevated)] transition">
                                <td className="p-3 font-mono text-xs text-[var(--text-secondary)] whitespace-nowrap">
                                  {ev.time_utc ? <>{ev.time_utc}<br /><span className="text-[var(--text-tertiary)]">UTC</span></> : "—"}
                                </td>
                                <td className="p-3 text-lg">{FLAGS[ev.currency] || "🌐"} <span className="text-xs font-mono text-[var(--text-tertiary)] align-middle">{ev.currency}</span></td>
                                <td className="p-3"><ImpactDots impact={ev.impact} /></td>
                                <td className="p-3">
                                  <button onClick={() => setExpandedRow(expandedRow === ev.id ? null : ev.id)} className="text-left w-full">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-bold text-[var(--text-primary)] hover:text-yellow-400 transition">{ev.title}</span>
                                      <LineChart className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
                                    </div>
                                    {expandedRow !== ev.id && (
                                      <div className="text-xs text-[var(--text-tertiary)] mt-0.5 line-clamp-1">{ev.description}</div>
                                    )}
                                  </button>
                                </td>
                                <td className="p-3 text-right font-mono text-xs text-[var(--text-primary)]">{ev.actual || "—"}</td>
                                <td className="p-3 text-right font-mono text-xs text-[var(--text-tertiary)]">{ev.forecast || "—"}</td>
                                <td className="p-3 text-right font-mono text-xs text-[var(--text-tertiary)]">{ev.previous || "—"}</td>
                                <td className="p-3">
                                  {user?.role === "admin" ? (
                                    <button onClick={() => removeEvent(ev.id)} className="text-[var(--text-tertiary)] hover:text-red-400 transition">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  ) : (
                                    <button onClick={() => setExpandedRow(expandedRow === ev.id ? null : ev.id)}
                                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-yellow-400/10 text-[var(--text-tertiary)] hover:text-yellow-400 transition">
                                      <ChevronDown className={"w-5 h-5 transition-transform " + (expandedRow === ev.id ? "rotate-180" : "")} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {expandedRow === ev.id && (
                                <tr className="bg-[var(--bg-elevated)]/50 border-b border-[var(--border-soft)] last:border-0">
                                  <td colSpan={8} className="p-4">
                                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{ev.description}</p>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                      </div>

                      {/* Mobile card view */}
                      <div className="md:hidden divide-y divide-[var(--border-soft)]">
                        {dayEvents.map((ev) => (
                          <div key={ev.id} className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="flex items-center gap-2 text-lg">
                                {FLAGS[ev.currency] || "🌐"}
                                <span className="text-xs font-mono text-[var(--text-tertiary)]">{ev.currency}</span>
                              </span>
                              <span className="font-mono text-xs text-[var(--text-secondary)]">
                                {ev.time_utc ? ev.time_utc + " UTC" : "—"}
                              </span>
                            </div>
                            <button onClick={() => setExpandedRow(expandedRow === ev.id ? null : ev.id)} className="text-left w-full">
                              <div className="flex items-start justify-between gap-2">
                                <span className="font-bold text-sm text-[var(--text-primary)]">{ev.title}</span>
                                <ImpactDots impact={ev.impact} />
                              </div>
                              {expandedRow === ev.id ? (
                                <p className="text-xs text-[var(--text-secondary)] mt-2 leading-relaxed">{ev.description}</p>
                              ) : (
                                <p className="text-xs text-[var(--text-tertiary)] mt-1 line-clamp-1">{ev.description}</p>
                              )}
                            </button>
                            <div className="flex items-center gap-4 mt-3 text-xs font-mono">
                              <span className="text-[var(--text-tertiary)]">Act: <span className="text-[var(--text-primary)]">{ev.actual || "—"}</span></span>
                              <span className="text-[var(--text-tertiary)]">Fcst: <span className="text-[var(--text-tertiary)]">{ev.forecast || "—"}</span></span>
                              <span className="text-[var(--text-tertiary)]">Prev: <span className="text-[var(--text-tertiary)]">{ev.previous || "—"}</span></span>
                              {user?.role === "admin" && (
                                <button onClick={() => removeEvent(ev.id)} className="ml-auto text-[var(--text-tertiary)] hover:text-red-400 transition">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                    )}
                  </div>
                );
              })
            )}
            <p className="text-xs text-[var(--text-tertiary)] text-center pt-2">
              All times are in UTC. Economic events may change without prior notice. Past performance is not indicative of future results.
            </p>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-3 space-y-4">
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5">
              <h3 className="font-display font-bold text-sm text-[var(--text-primary)] mb-4">Impact Guide</h3>
              <div className="space-y-4">
                {[
                  { color: "#f87171", level: 3, label: "High Impact", desc: "Major market mover" },
                  { color: "#fb923c", level: 2, label: "Medium Impact", desc: "Moderate market mover" },
                  { color: "#facc15", level: 1, label: "Low Impact", desc: "Minor market mover" },
                ].map((m) => (
                  <div key={m.label} className="flex items-start gap-2.5">
                    <div className="flex gap-1 mt-1">
                      {[0, 1, 2].map((i) => (
                        <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: i < m.level ? m.color : "var(--border-soft)" }} />
                      ))}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-[var(--text-primary)]">{m.label}</div>
                      <div className="text-xs text-[var(--text-tertiary)]">{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5">
              <h3 className="font-display font-bold text-sm text-[var(--text-primary)] mb-4">Market Movers</h3>
              <div className="space-y-3">
                {MOVERS.map((m) => (
                  <div key={m.label} className="flex items-center gap-2.5">
                    <span className="text-base">{m.icon}</span>
                    <div>
                      <div className="text-xs font-bold text-[var(--text-primary)]">{m.label}</div>
                      <div className="text-[10px] text-[var(--text-tertiary)]">{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {highImpactUpcoming.length > 0 && (
              <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5">
                <h3 className="font-display font-bold text-sm text-[var(--text-primary)] mb-4">Upcoming High Impact</h3>
                <div className="space-y-3">
                  {highImpactUpcoming.map((ev) => (
                    <div key={ev.id} className="flex items-start gap-2.5">
                      <span className="text-lg flex-shrink-0">{FLAGS[ev.currency] || "🌐"}</span>
                      <div className="min-w-0">
                        <div className="text-xs font-mono text-[var(--text-tertiary)]">{ev.time_utc ? ev.time_utc + " UTC" : ""}</div>
                        <div className="text-xs font-bold text-[var(--text-primary)] truncate">{ev.title}</div>
                        <div className="text-[10px] text-[var(--text-tertiary)]">
                          {new Date(ev.event_date + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-yellow-400" />
                <h3 className="font-display font-bold text-sm text-[var(--text-primary)]">Stay Updated</h3>
              </div>
              <p className="text-xs text-[var(--text-tertiary)] mb-4">Get notified about high impact events.</p>
              <button className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-lg px-4 py-2.5 hover:bg-yellow-500 transition text-xs">
                <Bell className="w-3.5 h-3.5" /> Enable Notifications
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}